import { supabase as supabaseClient } from '../client';
import type { SupabaseClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';

export interface ConsentData {
  id: string;
  userId: string;
  title: string;
  isGiven: boolean;
  method: string;
  version?: string;
  ipAddress?: string;
  withdrawnAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateConsentInput {
  userId: string;
  title: string;
  isGiven?: boolean;
  method?: string;
  version?: string;
  ipAddress?: string;
}

export const consentService = {
  /**
   * Create a new consent record
   */
  async create(data: CreateConsentInput, client?: SupabaseClient): Promise<ConsentData | null> {
    const supabase = client || supabaseClient;
    
    const consentRecord = {
      id: uuidv4(),
      userId: data.userId,
      title: data.title,
      isGiven: data.isGiven ?? true,
      method: data.method || 'online',
      version: data.version,
      ipAddress: data.ipAddress,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const { data: consent, error } = await supabase
      .from('consents')
      .insert(consentRecord)
      .select()
      .single();
    
    if (error) {
      console.error('Error creating consent record:', error);
      return null;
    }
    
    return consent;
  },

  /**
   * Create multiple consent records at once
   */
  async createMultiple(records: CreateConsentInput[], client?: SupabaseClient): Promise<ConsentData[] | null> {
    const supabase = client || supabaseClient;
    
    const consentRecords = records.map(data => ({
      id: uuidv4(),
      userId: data.userId,
      title: data.title,
      isGiven: data.isGiven ?? true,
      method: data.method || 'online',
      version: data.version,
      ipAddress: data.ipAddress,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));
    
    console.log('[ConsentService] Attempting to insert consent records:', consentRecords);
    
    const { data: consents, error } = await supabase
      .from('consents')
      .insert(consentRecords)
      .select();
    
    if (error) {
      console.error('[ConsentService] Error creating consent records:', error);
      console.error('[ConsentService] Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      return null;
    }
    
    return consents;
  },

  /**
   * Save signup consents (Terms and Privacy only - marketing is stored in user table)
   */
  async saveSignupConsents(
    userId: string, 
    ipAddress?: string,
    client?: SupabaseClient
  ): Promise<boolean> {
    const supabase = client || supabaseClient;
    
    console.log('[ConsentService] Saving Terms and Privacy consents for user:', userId);
    
    const consents: CreateConsentInput[] = [
      {
        userId,
        title: 'Terms of Service',
        isGiven: true,
        method: 'online',
        version: '2025-08-10', // Version from the terms page
        ipAddress
      },
      {
        userId,
        title: 'Privacy Policy',
        isGiven: true,
        method: 'online',
        version: '2025-08-10', // Version from the privacy page
        ipAddress
      }
    ];
    
    const results = await this.createMultiple(consents, supabase);
    
    if (results === null) {
      console.error('[ConsentService] Failed to save consent records');
      return false;
    }
    
    console.log('[ConsentService] Successfully saved consent records:', results);
    return true;
  },

  /**
   * Get all consents for a user
   */
  async getUserConsents(userId: string, client?: SupabaseClient): Promise<ConsentData[]> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('consents')
      .select('*')
      .eq('userId', userId)
      .order('createdAt', { ascending: false });
    
    if (error) {
      console.error('Error fetching user consents:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Withdraw consent
   */
  async withdrawConsent(consentId: string, client?: SupabaseClient): Promise<boolean> {
    const supabase = client || supabaseClient;
    
    const { error } = await supabase
      .from('consents')
      .update({
        isGiven: false,
        withdrawnAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      .eq('id', consentId);
    
    if (error) {
      console.error('Error withdrawing consent:', error);
      return false;
    }
    
    return true;
  },

  /**
   * Check if user has given specific consent
   */
  async hasConsent(userId: string, title: string, client?: SupabaseClient): Promise<boolean> {
    const supabase = client || supabaseClient;
    
    const { data, error } = await supabase
      .from('consents')
      .select('isGiven')
      .eq('userId', userId)
      .eq('title', title)
      .eq('isGiven', true)
      .order('createdAt', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking consent:', error);
      return false;
    }
    
    return data?.isGiven === true;
  }
};